import { AlertCircle, CheckCircle2, Info, Sparkles, TriangleAlert, type LucideIcon } from "lucide-react";
import { useToast } from "@/app/hooks/use-toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";

/** Auto-dismiss window; the progress bar animation is synced to this. */
const TOAST_DURATION = 5000;

const VARIANT_ICON: Record<string, LucideIcon> = {
  default: Sparkles,
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  destructive: AlertCircle,
};

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, duration, ...props }) {
        const Icon = VARIANT_ICON[variant ?? "default"] ?? Sparkles;
        const ms = duration ?? TOAST_DURATION;
        return (
          <Toast key={id} variant={variant} duration={ms} {...props}>
            {/* accent rail */}
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 w-1"
              style={{ background: "var(--toast-accent)" }}
            />

            {/* icon chip */}
            <span
              aria-hidden
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{
                background: "color-mix(in srgb, var(--toast-accent) 14%, transparent)",
                border: "1px solid color-mix(in srgb, var(--toast-accent) 32%, transparent)",
                color: "var(--toast-accent)",
              }}
            >
              <Icon className="h-5 w-5" />
            </span>

            <div className="grid flex-1 gap-0.5 pl-0.5">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
              {action}
            </div>

            <ToastClose />

            {/* duration progress bar — pauses on hover with Radix's timer */}
            <span
              aria-hidden
              className="toast-progress absolute bottom-0 left-0 h-0.5 opacity-80 group-hover:[animation-play-state:paused]"
              style={{ background: "var(--toast-accent)", animationDuration: `${ms}ms` }}
            />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
